import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useAuthStore } from '@/store/modules/auth';
import { usePaymentStore } from '@/store/modules/payment';
import useTransactionsStore from '@/store/modules/transactions';
import { isValidWalletAddress, numberFormat } from '@/utils/helpers';
import { MIXPANEL_EVENTS, trackEvent } from '@/utils/mixpanel';
import { TransactTypes } from '@/types/transact';
import { PaymentTypes } from '@/types/payment';
var PaymentSteps = PaymentTypes.PaymentSteps;
export const useInterceptorStore = defineStore('interceptor', () => {
    const error = ref('');
    const hasQueryParams = ref(false);
    const hasSetChannels = ref(false);
    const loading = ref('');
    const pendingTransaction = ref(true);
    const auth = useAuthStore();
    const payments = usePaymentStore();
    const transactions = useTransactionsStore();
    const selectedCurrency = ref({
        currency: '',
        countryCode: '',
    });
    const authRefs = storeToRefs(auth);
    const paymentRefs = storeToRefs(payments);
    const txnRefs = storeToRefs(transactions);
    const showBackNavigation = ref(true);
    const setLoading = (value) => {
        loading.value = value || '';
    };
    const setError = (value) => {
        error.value = value || '';
    };
    const setQueryParams = (q) => {
        const requiredParams = {
            [TransactTypes.TransactionType.BUY]: ['currencyAmount', 'token'],
            [TransactTypes.TransactionType.SELL]: ['cryptoAmount', 'token'],
        };
        hasQueryParams.value = requiredParams[(q.txType ??
            TransactTypes.TransactionType.BUY)].every((element) => Object.keys(q).includes(element));
        if (!hasQueryParams.value) {
            console.error(`Missing required query params for txType ${q.txType}`);
        }
    };
    const breakFlow = () => {
        /* Resets params used to skip params */
        hasQueryParams.value = false;
        hasSetChannels.value = false;
        pendingTransaction.value = false;
        setLoading();
    };
    const isBuyTxn = computed(() => txnRefs.txType.value.toLocaleLowerCase() ===
        TransactTypes.TransactionType.BUY);
    const isSellTxn = computed(() => txnRefs.txType.value.toLocaleLowerCase() ===
        TransactTypes.TransactionType.SELL);
    const canProcessTxn = computed(() => authRefs.isLoggedIn.value &&
        pendingTransaction.value &&
        hasQueryParams.value);
    const hasMultipleChannels = computed(() => txnRefs.channels.value.length > 1);
    const setChannel = (c) => {
        txnRefs.channel.value = c;
        txnRefs.selectedChannel.value = txnRefs.channel.value.channelType;
        selectedCurrency.value = {
            currency: txnRefs.channel.value.currency,
            countryCode: txnRefs.channel.value.country,
        };
        transactions.setFees();
        hasSetChannels.value = true;
    };
    const setCountryConfig = () => {
        txnRefs.selectedCurrency.value = {
            currency: selectedCurrency.value.currency,
            editable: true,
        };
        transactions.setCountryConfig({
            currency: selectedCurrency.value.currency,
        });
    };
    const validateBuy = () => {
        if (!Number(txnRefs.selectedCurrencyAmount.value.amount)) {
            return setError('Enter a valid amount');
        }
        const currencyAmount = Number(txnRefs.selectedCurrencyAmount.value.amount);
        txnRefs.selectedCryptoAmount.value.amount =
            transactions.calculateCryptoAmount(currencyAmount);
        if (currencyAmount < txnRefs.minTransactionAmount.value) {
            return setError(`Please enter an amount greater than ${selectedCurrency.value.currency} ${numberFormat(txnRefs.minTransactionAmount.value)}`);
        }
        if (currencyAmount > txnRefs.maxTransactionAmount.value) {
            return setError(`Please enter an amount lower than ${selectedCurrency.value.currency} ${numberFormat(txnRefs.maxTransactionAmount.value)}`);
        }
        return true;
    };
    const validateSell = () => {
        const minMax = transactions.getMinMax();
        if (!minMax) {
            return setError('Transaction limits not set');
        }
        if (!Number(txnRefs.selectedCryptoAmount.value.amount)) {
            return setError('Enter a valid amount');
        }
        if (txnRefs.selectedCryptoAmount.value.amount < minMax.min) {
            return setError(`Please enter an amount greater than ${txnRefs.selectedCrypto.value.token} ${numberFormat(minMax.min, 6)}`);
        }
        if (txnRefs.selectedCryptoAmount.value.amount > minMax.max) {
            return setError(`Please enter an amount lower than ${txnRefs.selectedCrypto.value.token} ${numberFormat(minMax.max, 6)}`);
        }
        return true;
    };
    const validateAmount = () => {
        setError();
        if (isBuyTxn.value) {
            validateBuy();
        }
        if (isSellTxn.value) {
            validateSell();
        }
        return true;
    };
    const validateChannels = async () => {
        const channels = await transactions.getChannelsWithFees(txnRefs.txType.value);
        txnRefs.channels.value = channels.length ? channels : [];
        if (!txnRefs.channels.value.length) {
            txnRefs.currentStep.value = TransactTypes.TransactSteps.NO_CHANNELS;
            return txnRefs.channels.value;
        }
        if (hasMultipleChannels.value) {
            let defaultChannel;
            if (txnRefs.selectedChannelId.value) {
                defaultChannel = channels.find((c) => txnRefs.selectedChannelId.value === c.id);
            }
            if (txnRefs.selectedChannel.value && !defaultChannel) {
                defaultChannel = channels.find((c) => txnRefs.selectedChannel.value === c.channelType);
            }
            if (defaultChannel) {
                setChannel(defaultChannel);
            }
            else {
                return (txnRefs.currentStep.value = TransactTypes.TransactSteps.BUY_2);
            }
        }
        else {
            setChannel(channels[0]);
        }
        if (hasSetChannels.value) {
            return (txnRefs.currentStep.value = TransactTypes.TransactSteps.BUY_3);
        }
        return txnRefs.channels.value;
    };
    const validateWalletAddress = () => {
        setError();
        if (!txnRefs.walletAddress.value.address)
            return false;
        const addressValid = isValidWalletAddress({
            address: txnRefs.walletAddress.value.address,
            token: txnRefs.selectedCrypto.value.token,
            standard: txnRefs.selectedCrypto.value.standard,
            networkType: txnRefs.selectedCrypto.value.networkType,
        });
        if (!addressValid) {
            txnRefs.walletAddress.value.editable = true;
            setError('Invalid wallet address and network pair');
        }
        return addressValid;
    };
    async function processBuyTxn() {
        setError();
        setLoading('Validating wallet');
        validateWalletAddress();
        setLoading();
        return (paymentRefs.currentStep.value = PaymentSteps.PAYMENT_1);
    }
    const setBackNavigation = (value) => (showBackNavigation.value = value);
    watch(hasQueryParams, async (value) => {
        if (value && pendingTransaction.value) {
            setBackNavigation(false);
            setLoading('Processing order');
            txnRefs.rates.value = await transactions.getRates();
            validateAmount();
            if (error.value) {
                setLoading();
                return (txnRefs.currentStep.value = TransactTypes.TransactSteps.BUY_1);
            }
            await validateChannels();
        }
        return setLoading();
    });
    watch([authRefs.isLoggedIn], async () => {
        if (canProcessTxn.value && isBuyTxn.value) {
            trackEvent(MIXPANEL_EVENTS.BUY.BUY, {
                logged_in: authRefs.isLoggedIn.value,
                partner: authRefs.partnerId.value,
            });
            return processBuyTxn();
        }
        if (canProcessTxn.value && isSellTxn.value) {
            trackEvent(MIXPANEL_EVENTS.SELL.SELL, {
                logged_in: authRefs.isLoggedIn.value,
                partner: authRefs.partnerId.value,
            });
            setCountryConfig();
            return (pendingTransaction.value = false);
        }
        return false;
    });
    watch(error, value => {
        if (value) {
            breakFlow();
        }
    });
    return {
        error,
        hasQueryParams,
        loading,
        pendingTransaction,
        setQueryParams,
        showBackNavigation,
    };
});
export default useInterceptorStore;
